/** One role's block: the rate in force now, where it comes from, its history,
 *  and (for editors) the form to schedule a new rate. */
import { useEffect, useRef, useState } from 'react';

import type { StaffPayRates, StaffRoleRates } from '../../../api/staff-pay-rates';

import { PAY_RATE_ROLE_LABELS } from './pay-rate-format';
import PayRateHistoryRow from './PayRateHistoryRow';
import PayRateRoleHeader from './PayRateRoleHeader';
import SetRateForm, { type SetRateSubmit } from './SetRateForm';
import type { WriteResult } from './use-staff-pay-rates';

export default function PayRateRoleBlock({
  rates,
  payRates,
  busy,
  error,
  onSetRate,
  onRemove,
}: {
  rates: StaffRoleRates;
  payRates: StaffPayRates;
  busy: boolean;
  /** The message for an action on this role that failed. */
  error: string | null;
  onSetRate: SetRateSubmit;
  onRemove: (rateId: string) => Promise<WriteResult>;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const setRateRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const formWasOpen = useRef(false);
  const label = PAY_RATE_ROLE_LABELS[rates.role];

  useEffect(() => {
    if (!formOpen && formWasOpen.current) setRateRef.current?.focus();
    formWasOpen.current = formOpen;
  }, [formOpen]);

  async function remove(rateId: string) {
    const result = await onRemove(rateId);
    if (result.ok) headingRef.current?.focus();
  }

  return (
    <div role="group" aria-label={label} className="py-3">
      <PayRateRoleHeader
        label={label}
        current={rates.current}
        showSetRate={payRates.canEdit && !formOpen}
        onSetRate={() => setFormOpen(true)}
        headingRef={headingRef}
        setRateRef={setRateRef}
      />
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
      {formOpen && (
        <SetRateForm
          role={rates.role}
          today={payRates.today}
          earliest={payRates.earliestEffectiveFrom}
          busy={busy}
          onSubmit={onSetRate}
          onClose={() => setFormOpen(false)}
        />
      )}
      {rates.history.length > 0 && (
        <ul className="mt-2 divide-y divide-stone-100 dark:divide-stone-800">
          {rates.history.map((entry) => (
            <PayRateHistoryRow key={entry.id} entry={entry} busy={busy} onRemove={remove} />
          ))}
        </ul>
      )}
    </div>
  );
}
